import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;

const AddButton = styled.button`
  padding: 12px 24px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  fontSize: 16px;
  fontWeight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #0056b3;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const TableHeader = styled.th`
  background: #f8f9fa;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  margin-right: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  
  &.edit {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 15px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 5px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #555;
  margin-bottom: 5px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #0056b3;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #545b62;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  .message {
    font-size: 18px;
    margin-bottom: 8px;
  }
  
  .submessage {
    font-size: 14px;
    opacity: 0.7;
  }
`;

const SearchBox = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Owners = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    is_company: false,
    first_name: '',
    last_name: '',
    company_name: '',
    contact_person_name: '',
    phone: '',
    email: '',
    mailing_address_line1: '',
    mailing_address_line2: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip: '',
    tax_id: ''
  });

  useEffect(() => {
    loadOwners();
  }, []);

  const loadOwners = async () => {
    try {
      const ownersData = await showingbookerAPI.getOwners();
      setOwners(ownersData);
    } catch (error) {
      console.error('Error loading owners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOwner = () => {
    setEditingOwner(null);
    setFormData({
      is_company: false,
      first_name: '',
      last_name: '',
      company_name: '',
      contact_person_name: '',
      phone: '',
      email: '',
      mailing_address_line1: '',
      mailing_address_line2: '',
      mailing_city: '',
      mailing_state: '',
      mailing_zip: '',
      tax_id: ''
    });
    setShowModal(true);
  };

  const handleEditOwner = (owner) => {
    setEditingOwner(owner);
    setFormData(owner);
    setShowModal(true);
  };

  const handleDeleteOwner = async (ownerId) => {
    if (window.confirm('Are you sure you want to delete this owner?')) {
      try {
        await showingbookerAPI.deleteOwner(ownerId);
        await loadOwners();
      } catch (error) {
        console.error('Error deleting owner:', error);
        alert('Failed to delete owner: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOwner) {
        await showingbookerAPI.updateOwner(editingOwner.id, formData);
      } else {
        await showingbookerAPI.createOwner(formData);
      }
      setShowModal(false);
      await loadOwners();
    } catch (error) {
      console.error('Error saving owner:', error);
      alert('Failed to save owner: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter owners based on search term
  const filteredOwners = owners.filter(owner => {
    const searchLower = searchTerm.toLowerCase();
    const name = owner.is_company ? 
      owner.company_name : 
      `${owner.first_name} ${owner.last_name}`;
    
    return name.toLowerCase().includes(searchLower) ||
           (owner.email && owner.email.toLowerCase().includes(searchLower)) ||
           (owner.phone && owner.phone.includes(searchTerm));
  });

  if (loading) {
    return <Container>Loading owners...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>👤 Owners ({owners.length})</Title>
        <AddButton onClick={handleAddOwner}>
          ➕ Add Owner
        </AddButton>
      </Header>

      {owners.length > 0 && (
        <SearchBox
          type="text"
          placeholder="🔍 Search owners by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}

      {owners.length === 0 ? (
        <EmptyState>
          <div className="icon">👤</div>
          <div className="message">No owners added yet</div>
          <div className="submessage">Add property owners to reuse their information when creating properties</div>
        </EmptyState>
      ) : filteredOwners.length === 0 ? (
        <EmptyState>
          <div className="icon">🔍</div>
          <div className="message">No owners match your search</div>
          <div className="submessage">Try adjusting your search terms</div>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <TableHeader>Name/Entity</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredOwners.map(owner => (
              <TableRow key={owner.id}>
                <TableCell>
                  {owner.is_company ? 
                    owner.company_name : 
                    `${owner.first_name} ${owner.last_name}`
                  }
                </TableCell>
                <TableCell>
                  {owner.is_company ? 
                    'Company' : 
                    'Individual'
                  }
                </TableCell>
                <TableCell>{owner.phone || '—'}</TableCell>
                <TableCell>{owner.email || '—'}</TableCell>
                <TableCell>
                  <ActionButton 
                    className="edit" 
                    onClick={() => handleEditOwner(owner)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton 
                    className="delete" 
                    onClick={() => handleDeleteOwner(owner.id)}
                  >
                    Delete
                  </ActionButton>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add/Edit Owner Modal */}
      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingOwner ? 'Edit Owner' : 'Add New Owner'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                ✕
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Owner Type</Label>
                <Select
                  value={formData.is_company ? 'company' : 'individual'}
                  onChange={(e) => handleInputChange('is_company', e.target.value === 'company')}
                  required
                >
                  <option value="individual">Individual</option>
                  <option value="company">Company</option>
                </Select>
              </FormGroup>

              {formData.is_company ? (
                <>
                  <FormRow>
                    <FormGroup>
                      <Label>Company Name *</Label>
                      <Input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Contact Person (c/o)</Label>
                      <Input
                        type="text"
                        value={formData.contact_person_name}
                        onChange={(e) => handleInputChange('contact_person_name', e.target.value)}
                        placeholder="Contact person for this entity"
                      />
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label>Tax ID</Label>
                      <Input
                        type="text"
                        value={formData.tax_id}
                        onChange={(e) => handleInputChange('tax_id', e.target.value)}
                        placeholder="EIN or Tax ID"
                      />
                    </FormGroup>
                    <FormGroup>
                      {/* Empty for spacing */}
                    </FormGroup>
                  </FormRow>
                </>
              ) : (
                <FormRow>
                  <FormGroup>
                    <Label>First Name *</Label>
                    <Input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name *</Label>
                    <Input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      required
                    />
                  </FormGroup>
                </FormRow>
              )}

              <FormRow>
                <FormGroup>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Mailing Address Line 1</Label>
                <Input
                  type="text"
                  value={formData.mailing_address_line1}
                  onChange={(e) => handleInputChange('mailing_address_line1', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Mailing Address Line 2</Label>
                <Input
                  type="text"
                  value={formData.mailing_address_line2}
                  onChange={(e) => handleInputChange('mailing_address_line2', e.target.value)}
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    type="text"
                    value={formData.mailing_city}
                    onChange={(e) => handleInputChange('mailing_city', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>State</Label>
                  <Select
                    value={formData.mailing_state}
                    onChange={(e) => handleInputChange('mailing_state', e.target.value)}
                  >
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>ZIP Code</Label>
                <Input
                  type="text"
                  value={formData.mailing_zip}
                  onChange={(e) => handleInputChange('mailing_zip', e.target.value)}
                />
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit">
                  {editingOwner ? 'Update Owner' : 'Add Owner'}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Owners;